export const SAFETY_TIP_CATEGORIES = {
  GENERAL: 'general',
  TASK_POSTING: 'task_posting',
  MAKING_OFFERS: 'making_offers', 
  COMMUNICATION: 'communication',
  PAYMENT: 'payment',
  MEETING: 'meeting'
};

export const SAFETY_TIPS = {
  [SAFETY_TIP_CATEGORIES.GENERAL]: [
    {
      id: 'verify-identity',
      title: 'Verify Identity',
      content: 'Always verify the identity of who you\'re working with. Check profiles, reviews, and ask for references when needed.',
      icon: '🛡️',
      priority: 'high'
    },
    {
      id: 'trust-instincts',
      title: 'Trust Your Instincts',
      content: 'If something feels off or too good to be true, trust your gut. Don\'t proceed if you feel uncomfortable.',
      icon: '🤔',
      priority: 'high'
    },
    {
      id: 'keep-records',
      title: 'Keep Records',
      content: 'Document all agreements, communications, and transactions. Take photos/videos of work when relevant.',
      icon: '📝',
      priority: 'medium'
    }
  ],

  [SAFETY_TIP_CATEGORIES.TASK_POSTING]: [
    {
      id: 'clear-expectations',
      title: 'Set Clear Expectations',
      content: 'Be specific about what you need, when you need it, and your budget. Clear communication prevents misunderstandings.',
      icon: '🎯',
      priority: 'high'
    },
    {
      id: 'reasonable-timeline',
      title: 'Set Reasonable Timelines',
      content: 'Allow adequate time for quality work. Rushed jobs often lead to poor results or safety shortcuts.',
      icon: '⏰',
      priority: 'medium'
    },
    {
      id: 'check-insurance',
      title: 'Verify Insurance & Licenses',
      content: 'For tasks requiring licenses or insurance (electrical, plumbing, etc.), always verify credentials.',
      icon: '📋',
      priority: 'high'
    }
  ],

  [SAFETY_TIP_CATEGORIES.MAKING_OFFERS]: [
    {
      id: 'realistic-pricing',
      title: 'Price Realistically',
      content: 'Research fair pricing for the task. Extremely low bids may indicate inexperience or corner-cutting.',
      icon: '💰',
      priority: 'medium'
    },
    {
      id: 'read-carefully',
      title: 'Read Task Details Carefully',
      content: 'Understand exactly what\'s required before making an offer. Ask questions if anything is unclear.',
      icon: '👀',
      priority: 'high'
    },
    {
      id: 'highlight-experience',
      title: 'Showcase Your Experience',
      content: 'Highlight relevant experience and provide examples of similar work to build trust with task posters.',
      icon: '⭐',
      priority: 'medium'
    }
  ],

  [SAFETY_TIP_CATEGORIES.COMMUNICATION]: [
    {
      id: 'platform-communication',
      title: 'Keep Communication on Platform',
      content: 'Use Doitly\'s messaging system for all communications. This provides protection and records for both parties.',
      icon: '💬',
      priority: 'high'
    },
    {
      id: 'professional-tone',
      title: 'Stay Professional',
      content: 'Maintain a professional and respectful tone in all communications, even if disagreements arise.',
      icon: '🤝',
      priority: 'medium'
    },
    {
      id: 'respond-promptly',
      title: 'Respond Promptly',
      content: 'Quick responses build trust and show reliability. Communicate any delays or issues immediately.',
      icon: '⚡',
      priority: 'medium'
    }
  ],

  [SAFETY_TIP_CATEGORIES.PAYMENT]: [
    {
      id: 'secure-payment',
      title: 'Use Secure Payment Methods',
      content: 'Use traceable payment methods like bank transfers, PayPal, or Venmo. Avoid cash for large amounts.',
      icon: '🔒',
      priority: 'high'
    },
    {
      id: 'payment-terms',
      title: 'Agree on Payment Terms',
      content: 'Clearly agree on payment amounts, timing, and methods before starting work. Get it in writing.',
      icon: '📄',
      priority: 'high'
    },
    {
      id: 'milestone-payments',
      title: 'Consider Milestone Payments',
      content: 'For larger tasks, consider breaking payments into milestones to protect both parties.',
      icon: '🎯',
      priority: 'medium'
    },
    {
      id: 'avoid-upfront',
      title: 'Avoid Large Upfront Payments',
      content: 'Be cautious of requests for large upfront payments. Pay incrementally as work progresses.',
      icon: '⚠️',
      priority: 'high'
    }
  ],

  [SAFETY_TIP_CATEGORIES.MEETING]: [
    {
      id: 'public-meeting',
      title: 'Meet in Public First',
      content: 'For initial meetings, choose public locations. This is especially important when you don\'t know the person.',
      icon: '🏪',
      priority: 'high'
    },
    {
      id: 'tell-someone',
      title: 'Tell Someone Your Plans',
      content: 'Let a friend or family member know when and where you\'re meeting, especially for in-person tasks.',
      icon: '📱',
      priority: 'high'
    },
    {
      id: 'trust-neighborhood',
      title: 'Trust Your Neighborhood Knowledge',
      content: 'If you\'re unfamiliar with the location, research the area and consider meeting during daylight hours.',
      icon: '🗺️',
      priority: 'medium'
    },
    {
      id: 'bring-backup',
      title: 'Consider Bringing Someone',
      content: 'For larger tasks or unfamiliar situations, consider bringing a friend or colleague.',
      icon: '👥',
      priority: 'medium'
    }
  ]
};

export const CONTEXTUAL_SAFETY_TIPS = {
  // Tips shown based on user actions or page context
  POSTING_TASK: [
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.TASK_POSTING][0], // clear-expectations
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.PAYMENT][1], // payment-terms
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.GENERAL][0] // verify-identity
  ],
  
  MAKING_OFFER: [
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.MAKING_OFFERS][1], // read-carefully
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.COMMUNICATION][0], // platform-communication
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.PAYMENT][0] // secure-payment
  ],
  
  MESSAGING: [
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.COMMUNICATION][0], // platform-communication
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.GENERAL][1], // trust-instincts
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.MEETING][1] // tell-someone
  ],
  
  FIRST_TIME_USER: [
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.GENERAL][1], // trust-instincts
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.COMMUNICATION][0], // platform-communication
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.PAYMENT][0], // secure-payment
    SAFETY_TIPS[SAFETY_TIP_CATEGORIES.MEETING][0] // public-meeting
  ]
};

export const SAFETY_ALERTS = {
  // Critical alerts that should be shown prominently
  HIGH_PRIORITY: [
    {
      id: 'payment-scam-alert',
      title: 'Payment Scam Alert',
      content: 'Never send money upfront or share banking details with strangers. Legitimate taskers don\'t ask for money before starting work.',
      type: 'warning',
      showOn: ['task-details', 'messaging'],
      dismissible: true
    },
    {
      id: 'meet-safely',
      title: 'Meet Safely',
      content: 'Always meet in public places first. Let someone know your plans when meeting for in-person tasks.',
      type: 'info',
      showOn: ['messaging', 'task-acceptance'],
      dismissible: true
    }
  ]
};